import React, { useRef } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useTheme } from '../../context/ThemeContext';

const EditableElement = ({ 
    type = 'text', 
    section, 
    field, 
    stylePrefix: p, 
    label, 
    defaultStyles = {},
    children, 
    as: Tag = 'div', 
    className = '',
    enableThemeStyles = true,
    allowContentEdit = true
}) => {
    const { isEditMode, setSelectedElement, selectedElement, themeData: draftTheme, saveDraft } = useEditor();
    const { theme: dbTheme } = useTheme(); 
    const elementRef = useRef(null);

    const isSelected = selectedElement?.field === field && selectedElement?.stylePrefix === p;
    const theme = isEditMode ? { ...dbTheme, ...draftTheme } : dbTheme;

    const getStyles = () => {
        if (!enableThemeStyles) return defaultStyles;
        const styles = { ...defaultStyles };
        const num = (val) => parseInt(val?.toString().replace(/px|%/g, '')) || 0;

        // APLICA SEMPRE ESTILOS DE TEXTO
        if (theme[`${p}Color`]) styles.color = theme[`${p}Color`];
        if (theme[`${p}Size`]) styles.fontSize = `${num(theme[`${p}Size`])}px`;
        if (theme[`${p}Font`]) styles.fontFamily = theme[`${p}Font`];
        if (theme[`${p}Bold`]) styles.fontWeight = 'bold';
        if (theme[`${p}Italic`]) styles.fontStyle = 'italic';
        if (theme[`${p}Align`]) styles.textAlign = theme[`${p}Align`];
        
        // ESTILOS DE CAIXA
        if (type === 'box' || type === 'button') {
            if (theme[`${p}BgColor`]) {
                const hex = theme[`${p}BgColor`];
                const opacity = theme[`${p}Opacity`] !== undefined ? num(theme[`${p}Opacity`]) : 100;
                if (hex?.startsWith('#')) {
                    const r = parseInt(hex.slice(1, 3), 16);
                    const g = parseInt(hex.slice(3, 5), 16);
                    const b = parseInt(hex.slice(5, 7), 16);
                    styles.backgroundColor = `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                } else { styles.backgroundColor = hex; }
            }
            if (theme[`${p}Blur`]) styles.backdropFilter = `blur(${num(theme[`${p}Blur`])}px)`;
            if (theme[`${p}BorderColor`]) styles.border = `1px solid ${theme[`${p}BorderColor`]}`;
            if (theme[`${p}Radius`]) styles.borderRadius = `${num(theme[`${p}Radius`])}px`;
            if (theme[`${p}Padding`]) styles.padding = `${num(theme[`${p}Padding`])}px`;
        }
        return styles;
    };

    const handleClick = (e) => {
        if (!isEditMode) return;
        e.stopPropagation();
        setSelectedElement({ type, section, field, stylePrefix: p, label: label || field });
    };

    const handleBlur = (e) => {
        if (isEditMode && type !== 'box' && type !== 'button' && allowContentEdit) {
            // Lógica de limpeza simplificada: Como não há etiqueta, o innerText é o texto real.
            // O loop abaixo falha para estruturas complexas, por isso usamos o fallback seguro.
            let cleanText = "";
            e.target.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) cleanText += node.textContent;
            });
            
            // Fallback robusto: Pega todo o texto visível (agora sem "lixo" da etiqueta)
            if (!cleanText && e.target.innerText) {
                cleanText = e.target.innerText.trim();
            }
            
            if (children !== cleanText) {
                const [coll, docId] = section.split('/');
                if (coll && docId) saveDraft(coll, docId, { [field]: cleanText });
            }
        }
    };

    const styles = getStyles();
    const currentAlign = theme[`${p}Align`] || styles.textAlign || 'inherit';
    const isInline = type === 'button' || !enableThemeStyles;

    const editorStyles = isEditMode ? {
        cursor: type === 'text' && allowContentEdit ? 'text' : 'pointer',
        // Mantemos a borda azul para saberes o que estás a selecionar
        outline: isSelected ? '2px solid #2563eb' : '1px dashed rgba(0,0,0,0.1)',
        outlineOffset: '2px',
        position: 'relative',
        zIndex: isSelected ? 50 : 1
    } : {};

    return (
        <Tag
            ref={elementRef}
            onClick={handleClick}
            onBlur={handleBlur}
            contentEditable={isEditMode && isSelected && (type === 'text' || type === 'longtext') && allowContentEdit}
            suppressContentEditableWarning={true}
            className={className}
            style={{ 
                ...styles, 
                ...editorStyles,
                display: isInline ? 'inline-block' : 'block',
                width: isInline ? 'auto' : '100%',
                textAlign: currentAlign
            }}
        >
            {enableThemeStyles && !isInline ? (
                <div style={{ textAlign: currentAlign, width: '100%', display: 'block', pointerEvents: 'none' }}>
                    <span style={{ pointerEvents: 'auto' }}>{children}</span>
                </div>
            ) : (
                children
            )}

            {/* ETIQUETA AZUL REMOVIDA DAQUI PARA NÃO CAUSAR BUGS */}
        </Tag>
    );
};

export default EditableElement;